import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useForm, useFormState } from "react-final-form";
import { CompanyRole } from "../../../../auth/AuthProvider";

export type PayloadSaveConfirmation = {
  shouldConfirm: (values: Record<string, unknown>) => boolean;
  title: string;
  description: string;
  confirmText: string;
  declineText: string;
};

export type SaveBarProps = {
  documentId: string;
  initialComment: string;
  companyRole: string;
  addComment: (userDocumentId: string, comment: string) => void;
  canEditPayload: boolean;
  requiresPayloadSave?: boolean;
  onSavePayload?: (
    values: Record<string, unknown>,
  ) => Promise<string | void>;
  onSaveSuccess?: () => Promise<void> | void;
  payloadSaveConfirmation?: PayloadSaveConfirmation;
};

/**
 * Footer for a document accordion that owns a single Save action covering both
 * the compliance comment draft and the RFF main-payload draft from the
 * enclosing `Form`. Must be rendered inside that `Form`.
 *
 * Save is enabled when either the comment or the form is dirty (the latter
 * only when `canEditPayload` is true), or when an editable empty-id document
 * still needs its first payload save. On click it runs `onSavePayload`, then
 * uses its returned persisted id for a dirty comment, then
 * `onSaveSuccess` (typically a document refetch by the parent), and finally
 * resets the form. Errors surface inline so the caller can retry from the
 * same drafts.
 */
export function SaveBar(props: SaveBarProps) {
  const {
    documentId,
    initialComment,
    companyRole,
    addComment,
    canEditPayload,
    requiresPayloadSave = false,
    onSavePayload,
    onSaveSuccess,
    payloadSaveConfirmation,
  } = props;
  const form = useForm();
  const { values, dirty, invalid } = useFormState({
    subscription: { values: true, dirty: true, invalid: true },
  });
  const [comment, setComment] = useState(initialComment);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  // Sync the local comment draft back to the persisted value when the parent
  // refetches comments (e.g. after a successful save in another accordion).
  useEffect(() => {
    setComment(initialComment);
  }, [initialComment]);

  const requiresDocumentCreation = documentId === "";
  const commentDirty = comment !== initialComment;
  const payloadDirty =
    canEditPayload &&
    (dirty || requiresDocumentCreation || requiresPayloadSave);
  // Payload edits cannot save while RFF reports a validation error (e.g. a
  // file-picker field with kinds configured but no kind selected). Persisted
  // comments remain independent, but a pending document cannot save a comment
  // until its valid payload has produced a real document id.
  const payloadCanSave = payloadDirty && !invalid && !!onSavePayload;
  const commentCanSave =
    commentDirty && (!requiresDocumentCreation || payloadCanSave);
  const canSave = !saving && (commentCanSave || payloadCanSave);

  const isViewer = companyRole === CompanyRole.VIEWER;

  const saveDraft = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      let effectiveDocumentId = documentId;
      if (payloadCanSave && onSavePayload) {
        const savedDocumentId = await onSavePayload(
          values as Record<string, unknown>,
        );
        if (savedDocumentId) {
          effectiveDocumentId = savedDocumentId;
        }
      }
      if (requiresDocumentCreation && !effectiveDocumentId) {
        throw new Error("Document creation did not return an id");
      }
      if (commentDirty) {
        addComment(effectiveDocumentId, comment);
      }
      if (onSaveSuccess) {
        await onSaveSuccess();
      }
      // The parent's refetch refreshes initialValues, which triggers RFF
      // reinitialization. Force-reset to ensure stale drafts don't linger if
      // the parent's reload doesn't change reference equality.
      form.reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }, [
    addComment,
    comment,
    commentDirty,
    documentId,
    form,
    onSavePayload,
    onSaveSuccess,
    payloadCanSave,
    requiresDocumentCreation,
    values,
  ]);

  const handleSave = useCallback(() => {
    const currentValues = values as Record<string, unknown>;
    if (
      payloadCanSave &&
      payloadSaveConfirmation?.shouldConfirm(currentValues)
    ) {
      setError(null);
      setConfirmationOpen(true);
      return;
    }

    void saveDraft();
  }, [payloadCanSave, payloadSaveConfirmation, saveDraft, values]);

  const handleConfirmSave = useCallback(() => {
    setConfirmationOpen(false);
    void saveDraft();
  }, [saveDraft]);

  const handleDeclineSave = useCallback(() => {
    if (saving) return;
    setConfirmationOpen(false);
  }, [saving]);

  return (
    <Stack spacing={1} sx={{ mt: 4 }}>
      <Stack spacing={2} direction="row">
        <Stack flex={1}>
          <TextField
            fullWidth
            multiline
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            label="Compliance comment"
            disabled={isViewer || saving}
          />
        </Stack>
        <Stack justifyContent="center" flexShrink={0}>
          <Button disabled={!canSave} onClick={handleSave}>
            Save
          </Button>
        </Stack>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {payloadSaveConfirmation && (
        <Dialog
          open={confirmationOpen}
          maxWidth="xs"
          fullWidth
          onClose={saving ? undefined : handleDeclineSave}
        >
          <DialogTitle>{payloadSaveConfirmation.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {payloadSaveConfirmation.description}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ padding: "16px 24px" }}>
            <Button
              onClick={handleDeclineSave}
              disabled={saving}
              variant="outlined"
            >
              {payloadSaveConfirmation.declineText}
            </Button>
            <Button
              onClick={handleConfirmSave}
              disabled={saving}
              variant="contained"
            >
              {payloadSaveConfirmation.confirmText}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}
